// ══════════════════════════════════════════════════════════════════════════════
// NexGen Auth System
// ══════════════════════════════════════════════════════════════════════════════
//
// ROLES (highest → lowest privilege):
//   hassaan  — owner/super-admin. Only Hassaan + admin see User Management.
//   admin    — full access including User Management
//   analyst  — all analysis pages, no User Management
//   guest    — limited access: Dashboard, Alerts, Reports, Threat Intel only
//              cannot see User Management, cannot see Attack Analytics internals
//   viewer   — Dashboard, Alerts, Reports only
//
// DATASETS: stored in React context (memory only). Cleared on browser close.
//           Persist across page navigation within a session.
//
// GUEST accounts: created by hassaan/admin only. Stored in localStorage.
//                 Can have an expiry date. Readonly — cannot change settings.
// ══════════════════════════════════════════════════════════════════════════════

// ── Core built-in accounts (never deletable) ──────────────────────────────────
export const BUILTIN_USERS = [
  {
    id: 1,
    username: "hassaan",
    password: "NexGenMa3b00ch",   // ← change this after first login
    role: "hassaan",
    active: true,
    builtin: true,
    created: "2024-01-01",
    lastLogin: null,
  },
  {
    id: 2,
    username: "admin",
    password: "NexGenMa3b00ch",
    role: "admin",
    active: true,
    builtin: true,
    created: "2024-01-01",
    lastLogin: null,
  },
  {
    id: 3,
    username: "guest",
    password: "Guest_NexGen_2026",
    role: "guest",
    active: true,
    builtin: true,
    created: "2026-04-01",
    lastLogin: null,
    note: "Shared demo account for prospects",
  },
];

// ── Page access matrix ────────────────────────────────────────────────────────
export const PAGE_ACCESS = {
  "/":          ["hassaan", "admin", "analyst", "guest", "viewer"],
  "/analytics": ["hassaan", "admin", "analyst", "guest"],
  "/alerts":    ["hassaan", "admin", "analyst", "guest", "viewer"],
  "/reports":   ["hassaan", "admin", "analyst", "guest", "viewer"],
  "/intel":     ["hassaan", "admin", "analyst", "guest"],
  "/compliance": ["hassaan", "admin", "analyst", "guest"],
  "/users":     ["hassaan", "admin"],   // ← ONLY hassaan + admin
};

// ── Nav items (sidebar) ───────────────────────────────────────────────────────
export const NAV_ITEMS = [
  { path: "/",          label: "Dashboard",        icon: "⬡", desc: "Anomaly Detection"   },
  { path: "/analytics", label: "Attack Analytics", icon: "◈", desc: "Attack Intelligence" },
  { path: "/alerts",    label: "Cyber Alerts",     icon: "◉", desc: "Live Alert Feed"     },
  { path: "/reports",   label: "Reports",          icon: "◧", desc: "Incident Reports"    },
  { path: "/intel",     label: "Threat Intel",     icon: "◬", desc: "IP · Port · MITRE"  },
  { path: "/compliance", label: "Compliance Gaps",  icon: "⊡", desc: "NIST · IEC · ISO"    },
  { path: "/users",     label: "User Management",  icon: "◎", desc: "Access Control"      },
];

export const ROLE_COLORS = {
  hassaan: "#FF0033",
  admin:   "#FF3D57",
  analyst: "#00D4FF",
  guest:   "#FFB800",
  viewer:  "#A78BFA",
};

// ── Auth helpers ──────────────────────────────────────────────────────────────

export function getAllUsers() {
  const builtins = BUILTIN_USERS;
  try {
    const extra = JSON.parse(localStorage.getItem("nexgen_guest_accounts") || "[]");
    return [...builtins, ...extra];
  } catch {
    return builtins;
  }
}

export function authenticate(username, password) {
  const all = getAllUsers();
  const user = all.find(
    u => u.username.toLowerCase() === username.toLowerCase() &&
         u.password === password &&
         u.active !== false
  );
  if (!user) return null;

  // Check expiry for guest accounts
  if (user.expiresAt) {
    if (new Date(user.expiresAt) < new Date()) {
      return { expired: true };
    }
  }

  return user;
}

export function canAccess(role, path) {
  const allowed = PAGE_ACCESS[path] || [];
  return allowed.includes(role);
}

export function isOwner(role) {
  return role === "hassaan" || role === "admin";
}

export function saveGuestAccounts(accounts) {
  // Only save non-builtin accounts
  const toSave = accounts.filter(u => !u.builtin);
  localStorage.setItem("nexgen_guest_accounts", JSON.stringify(toSave));
}

export function getGuestAccounts() {
  try {
    return JSON.parse(localStorage.getItem("nexgen_guest_accounts") || "[]");
  } catch {
    return [];
  }
}
